import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  BriefcaseBusiness,
  CheckCircle2,
  GripVertical,
  Hammer,
  ImagePlus,
  Lock,
  Palette,
  PencilLine,
  Settings,
  Sparkles,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { resolveApiAssetUrl } from '../../../services/api';
import { DEFAULT_FLAG_COLOR, getFlagColorOption } from './businessCardFlagColors';

function formatSystemLabel(systemType) {
  if (!systemType) {
    return 'Business';
  }

  return systemType
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function BusinessPortalCard({
  business,
  isSelected,
  isBusinessOpen,
  isOpening = false,
  isLaunchable,
  onOpenBusiness,
  onOpenUserManagement,
  onSelectBusiness,
  onChangeBrandLogo,
  onChangeFlagColor,
  onRenameBusiness,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  isDragging,
  onDragStart,
  onDragEnd,
  onDragEnter,
  flagColor = DEFAULT_FLAG_COLOR,
}) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  const portalAccess = business.portal_access ?? business.portal_state ?? 'open';
  const isUnderConstruction = business.status === 'under_construction';
  const isUnavailable = !isBusinessOpen(business);
  const accessLabel = portalAccess === 'open' ? 'open' : 'locked';
  const overlayLabel = isUnderConstruction ? 'In Development' : 'Locked';
  const overlayMessage = isUnderConstruction
    ? 'This portal is currently being developed and will open once the setup is completed.'
    : 'This portal is not available yet. It will open once it has been activated.';
  const businessInitial = (business.name || '?').trim().charAt(0).toUpperCase();
  const systemLabel = formatSystemLabel(business.system_type);
  const selectedFlagColor = getFlagColorOption(flagColor);
  const headerStyle = selectedFlagColor.background ? { background: selectedFlagColor.background } : undefined;
  const selectedCardStyle =
    isSelected && selectedFlagColor.value !== DEFAULT_FLAG_COLOR
      ? {
          boxShadow: `0 24px 46px ${selectedFlagColor.selected}22`,
        }
      : undefined;
  const selectedRingStyle =
    isSelected && selectedFlagColor.value !== DEFAULT_FLAG_COLOR
      ? { borderColor: selectedFlagColor.selected }
      : undefined;

  useEffect(() => {
    if (!isSettingsOpen) {
      return undefined;
    }

    function handleDocumentClick(event) {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setIsSettingsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isSettingsOpen]);

  function handleSettingsAction(event, action) {
    event.stopPropagation();
    setIsSettingsOpen(false);
    action(business);
  }

  return (
    <article
      aria-pressed={isSelected}
      className={`group relative overflow-hidden rounded-[1.2rem] border text-left transition sm:rounded-[1.5rem] ${
        isSelected
          ? 'border-transparent bg-white shadow-[0_24px_46px_rgba(69,48,33,0.14)]'
          : 'border-[#e9e1d9] bg-white shadow-[0_16px_34px_rgba(69,48,33,0.08)] hover:-translate-y-0.5 hover:border-[#d9cec3] hover:shadow-[0_22px_40px_rgba(69,48,33,0.12)]'
      } ${isUnavailable ? 'bg-[#fbf8f4]' : ''} ${isDragging ? 'scale-[0.99] opacity-70' : ''}`}
      draggable
      onClick={() => onSelectBusiness(business)}
      onDragEnd={onDragEnd}
      onDragEnter={onDragEnter}
      onDragOver={(event) => event.preventDefault()}
      onDragStart={onDragStart}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelectBusiness(business);
        }
      }}
      role="button"
      style={selectedCardStyle}
      tabIndex={0}
    >
      {isSelected ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[35] rounded-[inherit] border border-[#8a6a55]"
          style={selectedRingStyle}
        />
      ) : null}

      <div
        className={`absolute inset-x-0 top-0 h-[5.75rem] sm:h-[6.25rem] ${
          isSelected
            ? 'bg-[radial-gradient(circle_at_top_left,_rgba(176,136,98,0.30),_transparent_58%),linear-gradient(180deg,_#f8efe3_0%,_#f4ebe3_62%,_rgba(255,255,255,0.78)_88%,_#ffffff_100%)]'
            : 'bg-[radial-gradient(circle_at_top_left,_rgba(176,136,98,0.18),_transparent_58%),linear-gradient(180deg,_#faf6f1_0%,_#f7f2ec_58%,_rgba(255,255,255,0.78)_88%,_#ffffff_100%)]'
        }`}
        style={headerStyle}
      />
      <div className="absolute -right-8 top-5 h-16 w-16 rounded-full bg-[rgba(196,164,132,0.12)] blur-2xl sm:top-6 sm:h-20 sm:w-20" />

      {isUnavailable ? (
        <div
          className={`pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center ${
            isUnderConstruction ? 'bg-[#fff9ee]/58' : 'bg-white/54'
          } backdrop-blur-[3px]`}
        >
          <span
            className={`inline-flex h-16 w-16 items-center justify-center rounded-full border bg-white/92 shadow-[0_12px_24px_rgba(69,48,33,0.08)] ${
              isUnderConstruction
                ? 'border-[#f3e2bc] text-[#a36a18]'
                : 'border-[#e8dfd7] text-[#7d6858]'
            }`}
          >
            {isUnderConstruction ? <Hammer className="h-7 w-7" /> : <Lock className="h-7 w-7" />}
          </span>
          <span
            className={`mt-4 text-sm font-semibold uppercase tracking-[0.16em] ${
              isUnderConstruction ? 'text-[#a36a18]' : 'text-[#7d6858]'
            }`}
          >
            {overlayLabel}
          </span>
        </div>
      ) : null}

      <div className="relative z-[2] p-2.5 sm:p-3.5">
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          <span className="inline-flex items-center gap-1 rounded-full border border-white/70 bg-white/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6f6258] backdrop-blur-sm sm:gap-1.5 sm:px-2.5 sm:text-[11px]">
            <GripVertical className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            Reorder
          </span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-full border border-white/70 bg-white/75 p-0.5 shadow-[0_8px_18px_rgba(69,48,33,0.06)] sm:hidden">
              <button
                aria-label={`Move ${business.name} up`}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#6f5644] transition hover:bg-[#f4ede6] disabled:cursor-not-allowed disabled:opacity-35"
                disabled={!canMoveUp}
                onClick={(event) => {
                  event.stopPropagation();
                  onMoveUp();
                }}
                type="button"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                aria-label={`Move ${business.name} down`}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#6f5644] transition hover:bg-[#f4ede6] disabled:cursor-not-allowed disabled:opacity-35"
                disabled={!canMoveDown}
                onClick={(event) => {
                  event.stopPropagation();
                  onMoveDown();
                }}
                type="button"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </div>
            <span className="hidden items-center gap-1.5 rounded-full border border-white/70 bg-white/70 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b7a6d] backdrop-blur-sm sm:inline-flex">
              <Sparkles className="h-3.5 w-3.5" />
              Portal
            </span>
          </div>
        </div>

        <div className={`mt-2 flex items-start justify-between gap-2.5 sm:mt-2.5 sm:gap-3 ${isUnavailable ? 'blur-[1.5px]' : ''}`}>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5 sm:gap-3">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[0.9rem] border border-white/70 bg-white/85 text-[13px] font-semibold text-[#5b4535] shadow-[0_10px_24px_rgba(91,69,53,0.10)] backdrop-blur-sm sm:h-10 sm:w-10 sm:rounded-[1rem] sm:text-sm">
                {business.logo ? (
                  <img
                    className="h-full w-full object-cover"
                    src={resolveApiAssetUrl(business.logo)}
                    alt={`${business.name} logo`}
                  />
                ) : (
                  businessInitial
                )}
              </span>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.12em] text-[#9a8a7d] sm:text-[11px]">
                  {systemLabel}
                </p>
                <h3 className="line-clamp-2 text-[15px] font-semibold tracking-[-0.03em] text-[#1a1511] sm:text-lg">
                  {business.name}
                </h3>
              </div>
            </div>
          </div>

          <div className="relative flex shrink-0 items-center gap-1.5" ref={settingsRef}>
            {isUnavailable ? (
              <span
                className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.9rem] border bg-white/85 shadow-[0_10px_20px_rgba(69,48,33,0.08)] sm:h-9 sm:w-9 sm:rounded-[1rem] ${
                  isUnderConstruction
                    ? 'border-[#f3e2bc] text-[#a36a18]'
                    : 'border-[#e8dfd7] text-[#7d6858]'
                }`}
              >
                {isUnderConstruction ? <Hammer className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <Lock className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
              </span>
            ) : isSelected ? (
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.9rem] bg-[#edf7ef] text-[#4e7b56] shadow-[0_10px_18px_rgba(78,123,86,0.14)] sm:h-9 sm:w-9 sm:rounded-[1rem]">
                <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </span>
            ) : (
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.9rem] bg-white/80 text-[#6f6258] shadow-[0_10px_18px_rgba(111,98,88,0.10)] sm:h-9 sm:w-9 sm:rounded-[1rem]">
                <BriefcaseBusiness className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </span>
            )}

            <button
              aria-label={`Open settings for ${business.name}`}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.9rem] border border-white/70 bg-white/85 text-[#6f6258] shadow-[0_10px_18px_rgba(111,98,88,0.10)] transition hover:bg-white sm:h-9 sm:w-9 sm:rounded-[1rem]"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setIsSettingsOpen((currentValue) => !currentValue);
              }}
            >
              <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </button>

            {isSettingsOpen ? (
              <div className="absolute right-0 top-10 z-30 w-56 overflow-hidden rounded-[18px] border border-[#e6ddd4] bg-white py-1.5 shadow-[0_18px_34px_rgba(17,17,17,0.12)] sm:top-11">
                <button
                  className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#4b3b30] transition hover:bg-[#faf7f3]"
                  type="button"
                  onClick={(event) => handleSettingsAction(event, onRenameBusiness)}
                >
                  <PencilLine className="h-4 w-4 text-[#7d6858]" />
                  Rename Business
                </button>
                <button
                  className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#4b3b30] transition hover:bg-[#faf7f3]"
                  type="button"
                  onClick={(event) => handleSettingsAction(event, onChangeBrandLogo)}
                >
                  <ImagePlus className="h-4 w-4 text-[#7d6858]" />
                  Change Business Brand/Logo
                </button>
                <button
                  className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#4b3b30] transition hover:bg-[#faf7f3]"
                  type="button"
                  onClick={(event) => handleSettingsAction(event, onChangeFlagColor)}
                >
                  <Palette className="h-4 w-4 text-[#7d6858]" />
                  Select Portal Theme
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className={`mt-3 flex flex-wrap gap-1.5 sm:mt-3.5 sm:gap-2 ${isUnavailable ? 'blur-[1.5px]' : ''}`}>
          <span
            className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] sm:px-2.5 sm:text-[11px] ${
              isUnderConstruction
                ? 'border-[#f3e2bc] bg-[#fff4df] text-[#a36a18]'
                : business.status === 'active'
                  ? 'border-[#dbeadf] bg-[#edf7ef] text-[#4e7b56]'
                  : 'border-[#ece4db] bg-[#f5f2ee] text-[#93857b]'
            }`}
          >
            {business.status.replace('_', ' ')}
          </span>
          <span
            className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] sm:px-2.5 sm:text-[11px] ${
              accessLabel === 'open'
                ? 'border-[#dbeadf] bg-[#edf7ef] text-[#4e7b56]'
                : 'border-[#ece4db] bg-[#f4efea] text-[#7d6858]'
            }`}
          >
            {accessLabel}
          </span>
        </div>

        <div
          className={`mt-2 rounded-[0.9rem] border border-[#ede5dc] bg-[linear-gradient(180deg,_rgba(255,255,255,0.85),_rgba(248,244,239,0.95))] p-2 sm:mt-2.5 sm:rounded-[1rem] ${
            isUnavailable ? 'blur-[1.5px]' : ''
          }`}
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8f7e71] sm:text-[11px]">
            Workspace status
          </p>
          <p className="mt-1 text-[12px] leading-4.5 text-[#6f6258] sm:text-[13px] sm:leading-5">
            {isUnavailable ? overlayMessage : 'This portal is ready to use and available for your team.'}
          </p>
        </div>

        {isSelected && !isUnavailable ? (
          <div className="relative z-20 mt-2 grid grid-cols-2 gap-1.5 sm:mt-2.5 sm:gap-2">
            <button
              className="inline-flex w-full items-center justify-center gap-1 rounded-[0.8rem] border border-[#dfd2c5] bg-white px-2 py-1.5 text-[12px] font-semibold text-[#5f4a3b] shadow-[0_8px_18px_rgba(111,86,68,0.07)] transition hover:bg-[#faf6f2] sm:rounded-[0.9rem] sm:px-3 sm:py-2 sm:text-[13px]"
              onClick={(event) => {
                event.stopPropagation();
                onOpenUserManagement(business);
              }}
              type="button"
            >
              User Management
            </button>
            <button
              className="inline-flex w-full items-center justify-center gap-1 rounded-[0.8rem] bg-[linear-gradient(135deg,_#7a5d4a,_#5f4a3b)] px-2 py-1.5 text-[12px] font-semibold text-white shadow-[0_12px_22px_rgba(111,86,68,0.16)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:bg-[#d8d1ca] disabled:text-[#7f736b] sm:rounded-[0.9rem] sm:px-3 sm:py-2 sm:text-[13px]"
              disabled={!isLaunchable || isOpening}
              onClick={(event) => {
                event.stopPropagation();
                onOpenBusiness(business);
              }}
              type="button"
            >
              {isOpening ? 'Opening portal...' : isLaunchable ? 'Open Portal' : 'Not connected'}
              {!isOpening ? <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> : null}
            </button>
          </div>
        ) : (
          <div className="mt-2 flex items-center justify-between rounded-[0.85rem] border border-dashed border-[#e7ddd3] bg-white/60 px-2.5 py-1.5 text-[12px] text-[#8b7a6d] sm:mt-2.5 sm:rounded-[0.95rem] sm:px-3 sm:py-2 sm:text-[13px]">
            <span>{isSelected ? 'Selected workspace' : 'Select this workspace'}</span>
            <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          </div>
        )}
      </div>
    </article>
  );
}

export default BusinessPortalCard;
